import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const AREA_META = {
  health: { label: 'Health', color: '#4caf7d' },
  mind: { label: 'Mind', color: '#9b5ce0' },
  career: { label: 'Career & Growth', color: '#c9a84c' },
  relationships: { label: 'Relationships', color: '#e05c5c' },
  faith: { label: 'Faith', color: '#5c8fe0' },
  purpose: { label: 'Goals', color: '#e07d3c' },
}

const HABITS = [
  { key: 'worked_out', label: 'Worked out' },
  { key: 'read_bible', label: 'Read Bible / journaled' },
  { key: 'reviewed_finances', label: 'Reviewed finances' },
  { key: 'meditated', label: 'Meditated / prayed' },
  { key: 'reached_out', label: 'Reached out to someone' },
  { key: 'worked_on_goal', label: 'Worked on main goal' },
]

export default function WeeklyReportPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const [weekData, setWeekData] = useState([])
  const [aiSummary, setAiSummary] = useState('')
  const [loadingAI, setLoadingAI] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => { if (user) loadWeekData() }, [user])

  async function loadWeekData() {
    setLoading(true)
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 6)
    const startStr = `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}`

    const { data } = await supabase
      .from('manual_tasks')
      .select('date, area, final_points')
      .eq('user_id', user.id)
      .eq('flagged', false)
      .gte('date', startStr)
      .order('date', { ascending: true })

    if (!data || data.length === 0) { setWeekData([]); setLoading(false); return }

    // Group by date
    const byDate = {}
    data.forEach(t => {
      if (!byDate[t.date]) byDate[t.date] = { date: t.date, health: 0, mind: 0, career: 0, relationships: 0, faith: 0, purpose: 0 }
      if (byDate[t.date][t.area] !== undefined) byDate[t.date][t.area] += t.final_points
    })

    const rows = Object.values(byDate).map(d => {
      const areas = [d.health, d.mind, d.career, d.relationships, d.faith, d.purpose]
      const overall = Math.min(100, Math.round(areas.reduce((a, b) => a + b, 0) / 6))
      return {
        date: d.date,
        overall_score: overall,
        health_score: Math.min(100, d.health),
        mind_score: Math.min(100, d.mind),
        career_score: Math.min(100, d.career),
        relationships_score: Math.min(100, d.relationships),
        faith_score: Math.min(100, d.faith),
        purpose_score: Math.min(100, d.purpose),
        worked_out: d.health > 0,
        read_bible: d.faith > 0,
        reviewed_finances: d.career > 0,
        meditated: d.mind > 0,
        reached_out: d.relationships > 0,
        worked_on_goal: d.purpose > 0,
      }
    })

    setWeekData(rows)
    setLoading(false)
  }

  const scores = weekData.map(d => d.overall_score || 0).filter(s => s > 0)
  const avgScore = scores.length ? Math.round(scores.reduce((a,b) => a+b,0) / scores.length) : 0
  const bestDay = weekData.length ? weekData.reduce((best, d) => (d.overall_score || 0) > (best.overall_score || 0) ? d : best, weekData[0]) : null
  const worstDay = weekData.length ? weekData.reduce((worst, d) => (d.overall_score || 0) < (worst.overall_score || 0) ? d : worst, weekData[0]) : null

  // Area averages
  const areaAvgs = {}
  Object.keys(AREA_META).forEach(area => {
    const key = `${area}_score`
    const vals = weekData.map(d => d[key] || 0).filter(v => v > 0)
    areaAvgs[area] = vals.length ? Math.round(vals.reduce((a,b) => a+b,0) / vals.length) : 0
  })

  // Habit completion rates
  const habitRates = HABITS.map(h => {
    const done = weekData.filter(d => d[h.key]).length
    return { ...h, done, total: weekData.length, pct: weekData.length ? Math.round(done/weekData.length*100) : 0 }
  })

  function formatDate(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  async function getAISummary() {
    setLoadingAI(true)
    const prompt = `You are a direct life coach. Write a brief weekly summary for this user based on their data. Be personal, direct, and motivating. Max 3 sentences.

Weekly data:
- Days logged: ${weekData.length}/7
- Average score: ${avgScore}/100
- Best day: ${bestDay ? formatDate(bestDay.date) + ' (' + bestDay.overall_score + ')' : 'N/A'}
- Worst day: ${worstDay ? formatDate(worstDay.date) + ' (' + worstDay.overall_score + ')' : 'N/A'}
- Area averages: ${JSON.stringify(areaAvgs)}
- Best habit: ${habitRates.sort((a,b) => b.pct-a.pct)[0]?.label} (${habitRates.sort((a,b) => b.pct-a.pct)[0]?.pct}%)
- Worst habit: ${habitRates.sort((a,b) => a.pct-b.pct)[0]?.label} (${habitRates.sort((a,b) => a.pct-b.pct)[0]?.pct}%)
User: ${profile?.full_name?.split(' ')[0] || 'there'}

Reply with ONLY the summary text, no quotes or labels.`

    try {
      const res = await fetch('/api/coach', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      })
      const data = await res.json()
      setAiSummary(data.text || '')
    } catch (e) { setAiSummary('') }
    setLoadingAI(false)
  }

  useEffect(() => { if (!loading && weekData.length > 0) getAISummary() }, [loading])

  return (
    <div style={s.page}>
      <div style={s.ambient} />
      <div style={s.header}>
        <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={s.headerTitle}>Weekly Report</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.main}>
        {loading ? (
          <div style={s.loading}>Loading your week...</div>
        ) : weekData.length === 0 ? (
          <div style={s.empty}>No check-ins this week yet. Start logging daily!</div>
        ) : (
          <>
            {/* WEEK HEADER */}
            <div style={s.weekHeader}>
              <div style={s.weekTitle}>This Week's Report</div>
              <div style={s.weekDays}>{weekData.length} of 7 days logged</div>
            </div>

            {/* AI SUMMARY */}
            <div style={s.aiCard}>
              <div style={s.aiEyebrow}>AI Coach Summary</div>
              {loadingAI ? (
                <div style={s.aiLoading}>Generating your summary...</div>
              ) : (
                <div style={s.aiText}>{aiSummary || 'Summary unavailable.'}</div>
              )}
            </div>

            {/* BEST & WORST */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Highlights</div>
              <div style={s.highlightRow}>
                <div style={s.highlightCard}>
                  <div style={s.highlightLabel}>Best Day</div>
                  <div style={{ ...s.highlightScore, color: '#4caf7d' }}>{bestDay?.overall_score || 0}</div>
                  <div style={s.highlightDate}>{formatDate(bestDay?.date)}</div>
                </div>
                <div style={s.highlightCard}>
                  <div style={s.highlightLabel}>Worst Day</div>
                  <div style={{ ...s.highlightScore, color: '#e05c5c' }}>{worstDay?.overall_score || 0}</div>
                  <div style={s.highlightDate}>{formatDate(worstDay?.date)}</div>
                </div>
              </div>
            </div>

            {/* AREA SCORES */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Area Averages</div>
              <div style={s.areaList}>
                {Object.entries(AREA_META).map(([area, meta]) => (
                  <div key={area} style={s.areaRow}>
                    <div style={s.areaLabel}>{meta.label}</div>
                    <div style={s.areaBarWrap}>
                      <div style={{ ...s.areaBar, width: `${areaAvgs[area]}%`, background: meta.color }} />
                    </div>
                    <div style={{ ...s.areaScore, color: meta.color }}>{areaAvgs[area]}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* HABIT COMPLETION */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Habit Completion</div>
              <div style={s.habitList}>
                {habitRates.sort((a,b) => b.pct - a.pct).map(h => (
                  <div key={h.key} style={s.habitRow}>
                    <div style={s.habitLabel}>{h.label}</div>
                    <div style={s.habitStats}>
                      <div style={s.habitBarWrap}>
                        <div style={{ ...s.habitBar, width: `${h.pct}%`, background: h.pct >= 70 ? '#4caf7d' : h.pct >= 40 ? '#c9a84c' : '#e05c5c' }} />
                      </div>
                      <div style={s.habitPct}>{h.pct}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* OVERALL */}
            <div style={s.overallCard}>
              <div style={s.overallLabel}>Weekly Average Score</div>
              <div style={s.overallScore}>{avgScore}</div>
              <div style={s.overallSub}>out of 100</div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.22) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(6,6,8,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#e8e6df' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '24px 20px 100px', position: 'relative', zIndex: 1 },
  loading: { textAlign: 'center', color: '#6b6860', padding: '40px 0' },
  empty: { textAlign: 'center', color: '#6b6860', padding: '40px 0', fontSize: '14px' },
  weekHeader: { marginBottom: '20px' },
  weekTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px', color: '#f0cc7a' },
  weekDays: { fontSize: '13px', color: '#6b6860', marginTop: '2px' },
  aiCard: { background: 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(201,168,76,0.03))', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '16px', padding: '20px', marginBottom: '24px' },
  aiEyebrow: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#c9a84c', marginBottom: '10px' },
  aiLoading: { fontSize: '13px', color: '#6b6860', fontStyle: 'italic' },
  aiText: { fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', fontSize: '15px', lineHeight: '1.7', color: '#e8e6df' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '12px' },
  highlightRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  highlightCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '18px', textAlign: 'center' },
  highlightLabel: { fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '8px' },
  highlightScore: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', lineHeight: 1 },
  highlightDate: { fontSize: '11px', color: '#6b6860', marginTop: '6px' },
  areaList: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  areaRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  areaLabel: { fontSize: '13px', color: '#e8e6df', width: '100px', flexShrink: 0 },
  areaBarWrap: { flex: 1, height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' },
  areaBar: { height: '100%', borderRadius: '3px', transition: 'width 0.8s ease' },
  areaScore: { fontSize: '14px', fontWeight: '600', width: '30px', textAlign: 'right', flexShrink: 0 },
  habitList: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  habitRow: { display: 'flex', flexDirection: 'column', gap: '6px' },
  habitLabel: { fontSize: '13px', color: '#e8e6df' },
  habitStats: { display: 'flex', alignItems: 'center', gap: '10px' },
  habitBarWrap: { flex: 1, height: '5px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' },
  habitBar: { height: '100%', borderRadius: '3px', transition: 'width 0.8s ease' },
  habitPct: { fontSize: '12px', color: '#6b6860', width: '32px', textAlign: 'right' },
  overallCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '24px', textAlign: 'center' },
  overallLabel: { fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '8px' },
  overallScore: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '72px', color: '#f0cc7a', lineHeight: 1 },
  overallSub: { fontSize: '13px', color: '#6b6860', marginTop: '4px' },
}
